// src/components/common/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { activeSubscription } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false); // Close mobile menu
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-md py-2' 
          : 'bg-transparent py-4 '
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                className="h-10 w-auto"
                src="https://lh3.googleusercontent.com/PkB2T4sPYeXyH1c6Uz1nPmebHH7F78LVZnDVyA2SzOEyHX2yY4KKjwLkUjOkgy1VPA=w1280-h800-e365-rj-sc0x00ffffff" // Replace with your logo URL
                alt="MedEra App"
              />
              <span className={`ml-3 font-bold text-xl ${scrolled ? 'text-primary-700' : 'text-stone-900'}`}>
                MedEra App
              </span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-200'
              } focus:outline-none`}
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <Link 
              to="/" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/courses" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Courses
            </Link>
            <Link 
              to="/experts" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Our Experts
            </Link>
            <Link 
              to="/subscription/plans" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Pricing & Plans
            </Link>
            <Link 
              to="/discount" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Discount
            </Link>
            <Link 
              to="/contact" 
              className={`text-base font-medium ${
                scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Auth buttons (desktop) */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {isAuthenticated ? (
              <>
                {activeSubscription && (
                  <Link
                    to="/subscription"
                    className={`text-base font-medium ${
                      scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="mr-1">Subscription</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>
                  </Link>
                )}
                <Link
                  to="/profile"
                  className={`text-base font-medium ${
                    scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
                  }`}
                >
                  My Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-base font-medium ${
                    scrolled ? 'text-gray-700 hover:text-primary-700' : 'text-stone-900 hover:text-primary-100'
                  }`}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-stone-900 bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Enroll Now
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${isOpen ? 'block' : 'hidden'} md:hidden`}
        id="mobile-menu"
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg rounded-b-lg">
          <Link
            to="/"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/courses"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Courses
          </Link>
          <Link
            to="/experts"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Our Experts
          </Link>
          <Link
            to="/subscription/plans"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Pricing & Plans
          </Link>
          <Link
            to="/discount"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Discount
          </Link>
          <Link
            to="/contact"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
            onClick={() => setIsOpen(false)}
          >
            Contact
          </Link>
          
          {/* Auth links (mobile) */}
          {isAuthenticated ? (
            <>
              {activeSubscription && (
                <Link
                  to="/subscription"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="flex items-center">
                    <span>Subscription</span>
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                </Link>
              )}
              <Link
                to="/profile"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
                onClick={() => setIsOpen(false)}
              >
                My Profile
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-700 hover:bg-primary-50"
                onClick={() => setIsOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/register"
                className="block px-3 py-2 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700"
                onClick={() => setIsOpen(false)}
              >
                Enroll Now
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;